import axios from "axios"

import { MULTIPART_HEADERS } from "../../common/axios"
import { paginateDemoList } from "./hooks/useAdminPaginatedQuery"
import {
  DEMO_ASSIGNMENTS,
  DEMO_AUDIT_LOGS,
  DEMO_CALLING_NUMBERS,
  DEMO_CALL_LOGS,
  DEMO_CONCIERGE_INBOX_SMS_TEMPLATES,
  DEMO_CONCIERGE_INBOX_TEMPLATES,
  DEMO_DASHBOARD,
  DEMO_INBOX_SMS_TEMPLATES,
  DEMO_INBOX_TEMPLATES,
  DEMO_MAILBOXES,
  DEMO_MLS_PURCHASES,
  DEMO_STAFF_PROFILE,
  DEMO_TOKEN,
  DEMO_TRIAL_MANAGEMENT,
  DEMO_USERS,
  DEMO_VOICEMAILS,
  DEMO_ZOHO_MAILBOX_AUDIT_LOGS,
  DEMO_ZOHO_ORG_CONNECTION,
  isAdminDemoMode,
} from "./mockData"

export const ADMIN_TOKEN_STORAGE_KEY = "smartsetter.admin.token"

export function getAdminToken() {
  return localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY)
}

export function getStaffAxiosClient(token = getAdminToken()) {
  const client = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: token ? { Authorization: `Token ${token}` } : {},
  })
  client.interceptors.response.use(
    (res) => res,
    (error) => {
      if (error?.response?.status === 401 && !isAdminDemoMode()) {
        localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY)
        if (
          window.location.pathname.startsWith("/admin") &&
          !window.location.pathname.includes("/log-in")
        ) {
          window.location.assign("/admin/log-in/")
        }
      }
      return Promise.reject(error)
    }
  )
  return client
}

async function withDemoFallback(apiCall, demoData, params = {}) {
  if (isAdminDemoMode()) {
    const list = Array.isArray(demoData) ? demoData : demoData?.results ?? []
    return { data: paginateDemoList(list, params) }
  }
  return apiCall()
}

export const adminApi = {
  login: (email, password) => {
    if (isAdminDemoMode()) {
      localStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, DEMO_TOKEN)
      return Promise.resolve({
        data: { ...DEMO_STAFF_PROFILE, token: DEMO_TOKEN },
      })
    }
    return axios
      .create({ baseURL: process.env.REACT_APP_API_URL })
      .post("/clients/api/staff/login/", {
        email: email.toLowerCase().trim(),
        password,
      })
      .then((response) => {
        localStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, response.data.token)
        return response
      })
  },

  logout: () => {
    if (isAdminDemoMode()) {
      localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY)
      return Promise.resolve({ status: 204 })
    }
    return getStaffAxiosClient()
      .post("/clients/api/staff/logout/")
      .finally(() => localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY))
  },

  fetchProfile: () =>
    withDemoFallback(
      () => getStaffAxiosClient().get("/clients/api/staff/profile/"),
      DEMO_STAFF_PROFILE
    ),

  fetchDashboard: () =>
    withDemoFallback(
      () => getStaffAxiosClient().get("/clients/api/staff/dashboard/"),
      DEMO_DASHBOARD
    ),

  fetchUsers: (params) =>
    withDemoFallback(
      () => getStaffAxiosClient().get("/clients/api/staff/users/", { params }),
      DEMO_USERS,
      params
    ),

  updateUser: (userId, payload) => {
    if (isAdminDemoMode()) {
      const user = DEMO_USERS.find((u) => u.id === userId) || DEMO_USERS[0]
      return Promise.resolve({
        data: { ...user, ...payload },
      })
    }
    return getStaffAxiosClient().patch(
      `/clients/api/staff/users/${userId}/`,
      payload
    )
  },

  createSmsEmployee: (payload) =>
    getStaffAxiosClient().post(
      "/clients/api/staff/users/sms-employees/",
      payload
    ),

  deleteUser: (userId) => {
    if (isAdminDemoMode()) {
      return Promise.resolve({ status: 204 })
    }
    return getStaffAxiosClient().delete(`/clients/api/staff/users/${userId}/`)
  },

  createConciergeUser: (payload) => {
    if (isAdminDemoMode()) {
      const nextId = Math.max(...DEMO_USERS.map((u) => u.id), 0) + 1
      const user = {
        id: nextId,
        email: payload.email,
        name: payload.name || "",
        is_concierge: true,
        is_onebase_staff: payload.is_onebase_staff ?? true,
        is_active: payload.is_active ?? true,
        active_assignment_count: 0,
        date_joined: new Date().toISOString(),
        last_login: null,
        login_link: `${(process.env.REACT_APP_API_URL || "").replace(
          /\/$/,
          ""
        )}/clients/concierge-magic-login/demo-${nextId}/`,
      }
      DEMO_USERS.unshift(user)
      return Promise.resolve({ status: 201, data: user })
    }
    return getStaffAxiosClient().post("/clients/api/staff/users/", payload)
  },

  createCustomerAccess: (payload) => {
    if (isAdminDemoMode()) {
      const nextId = Math.max(...DEMO_USERS.map((u) => u.id), 0) + 1
      const isActive = payload.status !== "Inactive"
      const user = {
        id: nextId,
        email: payload.email,
        name: `${payload.first_name} ${payload.last_name}`.trim(),
        first_name: payload.first_name,
        last_name: payload.last_name,
        job_title: payload.job_title,
        city: payload.city,
        state: payload.state,
        phone: payload.phone,
        is_concierge: false,
        is_onebase_staff: false,
        is_active: isActive,
        active_assignment_count: 0,
        date_joined: new Date().toISOString(),
        last_login: null,
        login_link: null,
        customer_login_link: isActive
          ? `${(process.env.REACT_APP_API_URL || "").replace(
              /\/$/,
              ""
            )}/clients/customer-magic-login/demo-${nextId}/`
          : null,
      }
      DEMO_USERS.unshift(user)
      return Promise.resolve({ status: 201, data: user })
    }
    return getStaffAxiosClient().post(
      "/clients/api/staff/customer-access/",
      payload
    )
  },

  fetchUser: (userId) => {
    if (isAdminDemoMode()) {
      const user = DEMO_USERS.find((u) => u.id === userId) || DEMO_USERS[0]
      return Promise.resolve({ data: user })
    }
    return getStaffAxiosClient().get(`/clients/api/staff/users/${userId}/`)
  },

  /**
   * Sign a login / MLS purchase link on demand (kind: customer | concierge |
   * mls_purchase). The users list no longer ships live links for every row.
   */
  issueUserLoginLink: (userId, kind) => {
    if (isAdminDemoMode()) {
      const base = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "")
      return Promise.resolve({
        data: {
          kind,
          url: `${base}/clients/${kind}-magic-login/demo-${userId}/`,
        },
      })
    }
    return getStaffAxiosClient().post(
      `/clients/api/staff/users/${userId}/login-link/`,
      { kind }
    )
  },

  searchAccounts: (search) =>
    withDemoFallback(
      () =>
        getStaffAxiosClient().get("/clients/api/staff/accounts/", {
          params: { search, limit: 20 },
        }),
      DEMO_USERS.filter((u) => !u.is_concierge)
    ),

  fetchConcierges: (params) =>
    withDemoFallback(
      () =>
        getStaffAxiosClient().get("/clients/api/staff/concierges/", { params }),
      DEMO_USERS.filter((u) => u.is_concierge),
      params
    ),

  fetchAssignments: (params) =>
    withDemoFallback(
      () =>
        getStaffAxiosClient().get("/clients/api/staff/concierge-assignments/", {
          params,
        }),
      DEMO_ASSIGNMENTS,
      params
    ),

  createAssignment: (payload) => {
    if (isAdminDemoMode()) {
      return Promise.resolve({
        status: 201,
        data: { assignments: [], errors: [] },
      })
    }
    return getStaffAxiosClient().post(
      "/clients/api/staff/concierge-assignments/",
      payload
    )
  },

  patchAssignment: (membershipId, payload) => {
    if (isAdminDemoMode()) {
      return Promise.resolve({ status: 200, data: {} })
    }
    return getStaffAxiosClient().patch(
      `/clients/api/staff/concierge-assignments/${membershipId}/`,
      payload
    )
  },

  revokeAssignment: (membershipId) => {
    if (isAdminDemoMode()) {
      return Promise.resolve({ status: 204 })
    }
    return getStaffAxiosClient().delete(
      `/clients/api/staff/concierge-assignments/${membershipId}/`
    )
  },

  exportAuditLogs: (params) =>
    getStaffAxiosClient().get(
      "/clients/api/staff/concierge-audit-logs/export/",
      {
        params,
        responseType: "blob",
      }
    ),

  fetchAuditLogs: (params) =>
    withDemoFallback(
      () =>
        getStaffAxiosClient().get("/clients/api/staff/concierge-audit-logs/", {
          params,
        }),
      DEMO_AUDIT_LOGS,
      params
    ),

  fetchMeetings: (params) =>
    withDemoFallback(
      () =>
        getStaffAxiosClient().get("/clients/api/staff/meetings/", { params }),
      [],
      params
    ),

  exportMeetings: (params) =>
    getStaffAxiosClient().get("/clients/api/staff/meetings/export/", {
      params,
      responseType: "blob",
    }),

  fetchMeeting: (meetingId) =>
    getStaffAxiosClient().get(`/clients/api/staff/meetings/${meetingId}/`),

  fetchMeetingsCalendar: (params) =>
    getStaffAxiosClient().get("/clients/api/staff/meetings/calendar/", {
      params,
    }),

  fetchMeetingAnalytics: (params) =>
    getStaffAxiosClient().get("/clients/api/staff/meetings/analytics/", {
      params,
    }),

  fetchMeetingHosts: (params) =>
    getStaffAxiosClient().get("/clients/api/staff/meeting-hosts/", { params }),

  fetchMeetingLiaisons: (params) =>
    getStaffAxiosClient().get("/clients/api/staff/meeting-liaisons/", {
      params,
    }),

  fetchMeetingCandidates: (params) =>
    getStaffAxiosClient().get("/clients/api/staff/meeting-candidates/", {
      params,
    }),

  fetchMeetingAvailability: (params) =>
    getStaffAxiosClient().get("/clients/api/staff/meeting-availability/", {
      params,
    }),

  createMeeting: (payload) =>
    getStaffAxiosClient().post("/clients/api/staff/meetings/", payload),

  fetchMeetingForm: (params) =>
    getStaffAxiosClient().get("/clients/api/staff/meeting-form/", { params }),

  fetchMeetingQuestions: () =>
    getStaffAxiosClient().get("/clients/api/staff/meeting-questions/"),

  createMeetingQuestion: (payload) =>
    getStaffAxiosClient().post(
      "/clients/api/staff/meeting-questions/",
      payload
    ),

  updateMeetingQuestion: (questionId, payload) =>
    getStaffAxiosClient().patch(
      `/clients/api/staff/meeting-questions/${questionId}/`,
      payload
    ),

  deleteMeetingQuestion: (questionId) =>
    getStaffAxiosClient().delete(
      `/clients/api/staff/meeting-questions/${questionId}/`
    ),

  reorderMeetingQuestions: (ids) =>
    getStaffAxiosClient().post(
      "/clients/api/staff/meeting-questions/reorder/",
      {
        ids,
      }
    ),

  fetchMeetingReminders: () =>
    getStaffAxiosClient().get("/clients/api/staff/meeting-reminders/"),

  createMeetingReminder: (payload) =>
    getStaffAxiosClient().post(
      "/clients/api/staff/meeting-reminders/",
      payload
    ),

  updateMeetingReminder: (reminderId, payload) =>
    getStaffAxiosClient().patch(
      `/clients/api/staff/meeting-reminders/${reminderId}/`,
      payload
    ),

  deleteMeetingReminder: (reminderId) =>
    getStaffAxiosClient().delete(
      `/clients/api/staff/meeting-reminders/${reminderId}/`
    ),

  fetchMeetingTimeZones: (params) =>
    getStaffAxiosClient().get("/clients/api/staff/meeting-time-zones/", {
      params,
    }),

  createMeetingTimeZone: (payload) =>
    getStaffAxiosClient().post(
      "/clients/api/staff/meeting-time-zones/",
      payload
    ),

  updateMeetingTimeZone: (zoneId, payload) =>
    getStaffAxiosClient().patch(
      `/clients/api/staff/meeting-time-zones/${zoneId}/`,
      payload
    ),

  deleteMeetingTimeZone: (zoneId) =>
    getStaffAxiosClient().delete(
      `/clients/api/staff/meeting-time-zones/${zoneId}/`
    ),

  fetchBookingLinks: (userId) =>
    getStaffAxiosClient().get("/clients/api/staff/meeting-booking-links/", {
      params: { user_id: userId },
    }),

  createBookingLink: (payload) =>
    getStaffAxiosClient().post(
      "/clients/api/staff/meeting-booking-links/",
      payload
    ),

  revokeBookingLink: (linkId) =>
    getStaffAxiosClient().post(
      `/clients/api/staff/meeting-booking-links/${linkId}/revoke/`
    ),

  rescheduleMeeting: (meetingId, payload) =>
    getStaffAxiosClient().post(
      `/clients/api/staff/meetings/${meetingId}/reschedule/`,
      payload
    ),

  cancelMeeting: (meetingId, payload) =>
    getStaffAxiosClient().post(
      `/clients/api/staff/meetings/${meetingId}/cancel/`,
      payload
    ),

  exportCallingNumbers: (params) =>
    getStaffAxiosClient().get("/clients/api/staff/calling-numbers/export/", {
      params,
      responseType: "blob",
    }),

  fetchCallingNumbers: (params) =>
    withDemoFallback(
      () =>
        getStaffAxiosClient().get("/clients/api/staff/calling-numbers/", {
          params,
        }),
      DEMO_CALLING_NUMBERS,
      params
    ),

  updateCallingNumber: (assignmentId, payload) => {
    if (isAdminDemoMode()) {
      return Promise.resolve({ status: 200, data: {} })
    }
    return getStaffAxiosClient().patch(
      `/clients/api/staff/calling-numbers/${assignmentId}/`,
      payload
    )
  },

  deleteCallingNumber: (assignmentId) => {
    if (isAdminDemoMode()) {
      return Promise.resolve({ status: 200, data: {} })
    }
    return getStaffAxiosClient().delete(
      `/clients/api/staff/calling-numbers/${assignmentId}/`
    )
  },

  exportCallLogs: (params) =>
    getStaffAxiosClient().get("/clients/api/staff/call-logs/export/", {
      params,
      responseType: "blob",
    }),

  fetchCallLogs: (params) =>
    withDemoFallback(
      () =>
        getStaffAxiosClient().get("/clients/api/staff/call-logs/", { params }),
      DEMO_CALL_LOGS,
      params
    ),

  fetchActivityReportLogs: (params) =>
    withDemoFallback(
      () =>
        getStaffAxiosClient().get(
          "/clients/api/staff/concierge-activity-report-logs/",
          { params }
        ),
      [],
      params
    ),

  fetchReportAnalytics: (params = {}) =>
    withDemoFallback(
      () =>
        getStaffAxiosClient().get(
          "/clients/api/staff/concierge-report-analytics/",
          { params }
        ),
      {
        report_date:
          params.report_date || params.report_date_to || "2026-09-16",
        report_date_from: params.report_date_from || params.report_date,
        report_date_to: params.report_date_to || params.report_date,
        report_timezone: "America/New_York",
        reports_sent: 8,
        reports_pending: 2,
        reports_failed: 1,
        pending: [],
      }
    ),

  fetchActivityReportForm: (params) =>
    withDemoFallback(
      () =>
        getStaffAxiosClient().get(
          "/clients/api/staff/concierge-activity-reports/form/",
          { params }
        ),
      {
        report_date: params.report_date || "2026-09-16",
        form: {
          concierge_first_name: "Demo",
          concierge_last_name: "Concierge",
          concierge_email: "concierge@example.com",
          client_name: "Demo Client",
          report_date: params.report_date || "2026-09-16",
          total_dialer_sessions: 2,
          active_minutes: 45,
          total_calls: 12,
          total_connections: 5,
          conversations_30_seconds: 3,
          total_bookings: 1,
          dialing_hours: 0.75,
          notes: "Demo session notes.",
        },
      }
    ),

  sendReportManually: (payload) => {
    if (isAdminDemoMode()) {
      return Promise.resolve({
        status: 201,
        data: {
          report_date: payload.report_date,
          status: "sent",
          sent_at: new Date().toISOString(),
        },
      })
    }
    return getStaffAxiosClient().post(
      "/clients/api/staff/concierge-activity-reports/send/",
      payload
    )
  },

  fetchActivityFeed: (params) =>
    withDemoFallback(
      () =>
        getStaffAxiosClient().get("/clients/api/staff/activity-feed/", {
          params,
        }),
      [],
      params
    ),

  fetchActivityThread: (params) => {
    if (isAdminDemoMode()) {
      return Promise.resolve({ data: { messages: [], counterparty: null } })
    }
    return getStaffAxiosClient().get(
      "/clients/api/staff/activity-feed/thread/",
      { params }
    )
  },

  fetchVoicemails: (params) =>
    withDemoFallback(
      () =>
        getStaffAxiosClient().get("/clients/api/staff/inbound-voicemails/", {
          params,
        }),
      DEMO_VOICEMAILS,
      params
    ),

  fetchMailboxes: (params) =>
    withDemoFallback(
      () =>
        getStaffAxiosClient().get("/clients/api/staff/mailboxes/", { params }),
      DEMO_MAILBOXES,
      params
    ),

  updateMailbox: (mailboxId, payload) => {
    if (isAdminDemoMode()) {
      const index = DEMO_MAILBOXES.findIndex((item) => item.id === mailboxId)
      if (index >= 0) {
        DEMO_MAILBOXES[index] = { ...DEMO_MAILBOXES[index], ...payload }
      }
      return Promise.resolve({
        data: DEMO_MAILBOXES[index] || {},
      })
    }
    return getStaffAxiosClient().patch(
      `/clients/api/staff/mailboxes/${mailboxId}/`,
      payload
    )
  },

  deleteMailbox: (mailboxId) => {
    if (isAdminDemoMode()) {
      const index = DEMO_MAILBOXES.findIndex((item) => item.id === mailboxId)
      if (index >= 0) {
        DEMO_MAILBOXES[index] = {
          ...DEMO_MAILBOXES[index],
          status: "disabled",
          oauth_status: "disconnected",
        }
      }
      return Promise.resolve({
        data: DEMO_MAILBOXES[index] || {},
      })
    }
    return getStaffAxiosClient().delete(
      `/clients/api/staff/mailboxes/${mailboxId}/`
    )
  },

  fetchInboxTemplates: (params) =>
    withDemoFallback(
      () =>
        getStaffAxiosClient().get("/clients/api/staff/inbox-templates/", {
          params,
        }),
      DEMO_INBOX_TEMPLATES,
      params
    ),

  createInboxTemplate: (formData) => {
    if (isAdminDemoMode()) {
      const nextId = Math.max(...DEMO_INBOX_TEMPLATES.map((t) => t.id), 0) + 1
      const template = {
        id: nextId,
        name: formData.get("name") || "Untitled template",
        subject: formData.get("subject") || "",
        html_body: formData.get("html_body") || "",
        created: new Date().toISOString(),
        attachments: [],
        attachment_count: 0,
        is_global: true,
        approval_status: "pending",
        ai_generated: formData.get("ai_generated") === "true",
        ai_prompt: formData.get("ai_prompt") || "",
        approved_at: new Date().toISOString(),
        approved_by_email: DEMO_STAFF_PROFILE.email,
      }
      DEMO_INBOX_TEMPLATES.unshift(template)
      return Promise.resolve({ status: 201, data: template })
    }
    return getStaffAxiosClient().post(
      "/clients/api/staff/inbox-templates/",
      formData,
      MULTIPART_HEADERS
    )
  },

  updateInboxTemplate: (templateId, formData) => {
    if (isAdminDemoMode()) {
      const existing =
        DEMO_INBOX_TEMPLATES.find((t) => t.id === templateId) ||
        DEMO_INBOX_TEMPLATES[0]
      const template = {
        ...existing,
        name: formData.get("name") || existing.name,
        subject: formData.get("subject") ?? existing.subject,
        html_body: formData.get("html_body") ?? existing.html_body,
        ai_generated:
          formData.get("ai_generated") === "true" || existing.ai_generated,
        ai_prompt: formData.get("ai_prompt") || existing.ai_prompt || "",
        approval_status: "pending",
      }
      const index = DEMO_INBOX_TEMPLATES.findIndex((t) => t.id === templateId)
      if (index >= 0) DEMO_INBOX_TEMPLATES[index] = template
      return Promise.resolve({ data: template })
    }
    return getStaffAxiosClient().put(
      `/clients/api/staff/inbox-templates/${templateId}/`,
      formData,
      MULTIPART_HEADERS
    )
  },

  deleteInboxTemplate: (templateId) => {
    if (isAdminDemoMode()) {
      const index = DEMO_INBOX_TEMPLATES.findIndex((t) => t.id === templateId)
      if (index >= 0) DEMO_INBOX_TEMPLATES.splice(index, 1)
      return Promise.resolve({ status: 204 })
    }
    return getStaffAxiosClient().delete(
      `/clients/api/staff/inbox-templates/${templateId}/`
    )
  },

  generateInboxTemplate: (prompt) => {
    if (isAdminDemoMode()) {
      return Promise.resolve({
        data: {
          subject: "Thanks for reaching out",
          content:
            "Hi {{ first_name }}, thanks for writing in. A concierge will follow up shortly.",
        },
      })
    }
    return getStaffAxiosClient().get(
      "/clients/api/staff/inbox-templates/generate-from-prompt/",
      { params: { prompt } }
    )
  },

  approveInboxTemplate: (templateId) => {
    if (isAdminDemoMode()) {
      const template = DEMO_INBOX_TEMPLATES.find((t) => t.id === templateId)
      if (template) {
        template.approval_status = "approved"
        template.approved_by_email = DEMO_STAFF_PROFILE.email
        template.approved_at = new Date().toISOString()
      }
      return Promise.resolve({ data: template })
    }
    return getStaffAxiosClient().post(
      `/clients/api/staff/inbox-templates/${templateId}/approve/`
    )
  },

  rejectInboxTemplate: (templateId) => {
    if (isAdminDemoMode()) {
      const template = DEMO_INBOX_TEMPLATES.find((t) => t.id === templateId)
      if (template) {
        template.approval_status = "rejected"
        template.approved_by_email = DEMO_STAFF_PROFILE.email
        template.approved_at = new Date().toISOString()
      }
      return Promise.resolve({ data: template })
    }
    return getStaffAxiosClient().post(
      `/clients/api/staff/inbox-templates/${templateId}/reject/`
    )
  },

  fetchConciergeInboxTemplates: (params) =>
    withDemoFallback(
      () =>
        getStaffAxiosClient().get(
          "/clients/api/staff/concierge-inbox-templates/",
          { params }
        ),
      DEMO_CONCIERGE_INBOX_TEMPLATES,
      params
    ),

  approveConciergeInboxTemplate: (templateId) => {
    if (isAdminDemoMode()) {
      const template = DEMO_CONCIERGE_INBOX_TEMPLATES.find(
        (t) => t.id === templateId
      )
      if (template) {
        template.approval_status = "approved"
        template.approved_by_email = DEMO_STAFF_PROFILE.email
        template.approved_at = new Date().toISOString()
      }
      return Promise.resolve({ data: template })
    }
    return getStaffAxiosClient().post(
      `/clients/api/staff/concierge-inbox-templates/${templateId}/approve/`
    )
  },

  rejectConciergeInboxTemplate: (templateId) => {
    if (isAdminDemoMode()) {
      const template = DEMO_CONCIERGE_INBOX_TEMPLATES.find(
        (t) => t.id === templateId
      )
      if (template) {
        template.approval_status = "rejected"
        template.approved_by_email = DEMO_STAFF_PROFILE.email
        template.approved_at = new Date().toISOString()
      }
      return Promise.resolve({ data: template })
    }
    return getStaffAxiosClient().post(
      `/clients/api/staff/concierge-inbox-templates/${templateId}/reject/`
    )
  },

  fetchConciergeInboxSmsTemplates: (params) =>
    withDemoFallback(
      () =>
        getStaffAxiosClient().get(
          "/clients/api/staff/concierge-inbox-sms-templates/",
          { params }
        ),
      DEMO_CONCIERGE_INBOX_SMS_TEMPLATES,
      params
    ),

  approveConciergeInboxSmsTemplate: (templateId) => {
    if (isAdminDemoMode()) {
      const template = DEMO_CONCIERGE_INBOX_SMS_TEMPLATES.find(
        (t) => t.id === templateId
      )
      if (template) {
        template.approval_status = "approved"
        template.approved_by_email = DEMO_STAFF_PROFILE.email
        template.approved_at = new Date().toISOString()
      }
      return Promise.resolve({ data: template })
    }
    return getStaffAxiosClient().post(
      `/clients/api/staff/concierge-inbox-sms-templates/${templateId}/approve/`
    )
  },

  rejectConciergeInboxSmsTemplate: (templateId) => {
    if (isAdminDemoMode()) {
      const template = DEMO_CONCIERGE_INBOX_SMS_TEMPLATES.find(
        (t) => t.id === templateId
      )
      if (template) {
        template.approval_status = "rejected"
        template.approved_by_email = DEMO_STAFF_PROFILE.email
        template.approved_at = new Date().toISOString()
      }
      return Promise.resolve({ data: template })
    }
    return getStaffAxiosClient().post(
      `/clients/api/staff/concierge-inbox-sms-templates/${templateId}/reject/`
    )
  },

  fetchInboxSmsTemplates: (params) =>
    withDemoFallback(
      () =>
        getStaffAxiosClient().get("/clients/api/staff/inbox-sms-templates/", {
          params,
        }),
      DEMO_INBOX_SMS_TEMPLATES,
      params
    ),

  createInboxSmsTemplate: (payload) => {
    if (isAdminDemoMode()) {
      const nextId =
        Math.max(...DEMO_INBOX_SMS_TEMPLATES.map((t) => t.id), 0) + 1
      const template = {
        id: nextId,
        name: payload.name || "Untitled template",
        text: payload.text || "",
        created: new Date().toISOString(),
        is_global: true,
        approval_status: "pending",
        ai_generated: Boolean(payload.ai_generated),
        ai_prompt: payload.ai_prompt || "",
        approved_at: new Date().toISOString(),
        approved_by_email: DEMO_STAFF_PROFILE.email,
      }
      DEMO_INBOX_SMS_TEMPLATES.unshift(template)
      return Promise.resolve({ status: 201, data: template })
    }
    return getStaffAxiosClient().post(
      "/clients/api/staff/inbox-sms-templates/",
      payload
    )
  },

  updateInboxSmsTemplate: (templateId, payload) => {
    if (isAdminDemoMode()) {
      const existing =
        DEMO_INBOX_SMS_TEMPLATES.find((t) => t.id === templateId) ||
        DEMO_INBOX_SMS_TEMPLATES[0]
      const template = {
        ...existing,
        name: payload.name || existing.name,
        text: payload.text ?? existing.text,
        ai_generated: Boolean(payload.ai_generated) || existing.ai_generated,
        ai_prompt: payload.ai_prompt || existing.ai_prompt || "",
        approval_status: "pending",
      }
      const index = DEMO_INBOX_SMS_TEMPLATES.findIndex(
        (t) => t.id === templateId
      )
      if (index >= 0) DEMO_INBOX_SMS_TEMPLATES[index] = template
      return Promise.resolve({ data: template })
    }
    return getStaffAxiosClient().put(
      `/clients/api/staff/inbox-sms-templates/${templateId}/`,
      payload
    )
  },

  deleteInboxSmsTemplate: (templateId) => {
    if (isAdminDemoMode()) {
      const index = DEMO_INBOX_SMS_TEMPLATES.findIndex(
        (t) => t.id === templateId
      )
      if (index >= 0) DEMO_INBOX_SMS_TEMPLATES.splice(index, 1)
      return Promise.resolve({ status: 204 })
    }
    return getStaffAxiosClient().delete(
      `/clients/api/staff/inbox-sms-templates/${templateId}/`
    )
  },

  generateInboxSmsTemplate: (prompt) => {
    if (isAdminDemoMode()) {
      return Promise.resolve({
        data: {
          title: "Thanks for texting",
          content:
            "Hi {{ first_name }}, thanks for texting. A concierge will follow up shortly.",
        },
      })
    }
    return getStaffAxiosClient().get(
      "/clients/api/staff/inbox-sms-templates/generate-from-prompt/",
      { params: { prompt } }
    )
  },

  approveInboxSmsTemplate: (templateId) => {
    if (isAdminDemoMode()) {
      const template = DEMO_INBOX_SMS_TEMPLATES.find((t) => t.id === templateId)
      if (template) {
        template.approval_status = "approved"
        template.approved_by_email = DEMO_STAFF_PROFILE.email
        template.approved_at = new Date().toISOString()
      }
      return Promise.resolve({ data: template })
    }
    return getStaffAxiosClient().post(
      `/clients/api/staff/inbox-sms-templates/${templateId}/approve/`
    )
  },

  rejectInboxSmsTemplate: (templateId) => {
    if (isAdminDemoMode()) {
      const template = DEMO_INBOX_SMS_TEMPLATES.find((t) => t.id === templateId)
      if (template) {
        template.approval_status = "rejected"
        template.approved_by_email = DEMO_STAFF_PROFILE.email
        template.approved_at = new Date().toISOString()
      }
      return Promise.resolve({ data: template })
    }
    return getStaffAxiosClient().post(
      `/clients/api/staff/inbox-sms-templates/${templateId}/reject/`
    )
  },

  fetchZohoOrgConnection: () => {
    if (isAdminDemoMode()) {
      return Promise.resolve({ data: DEMO_ZOHO_ORG_CONNECTION })
    }
    return getStaffAxiosClient().get("/clients/api/staff/zoho-mail-org/")
  },

  fetchZohoMailboxAuditLogs: (params) =>
    withDemoFallback(
      () =>
        getStaffAxiosClient().get(
          "/clients/api/staff/zoho-mailbox-audit-logs/",
          {
            params,
          }
        ),
      DEMO_ZOHO_MAILBOX_AUDIT_LOGS,
      params
    ),

  fetchTrialManagement: (params) =>
    withDemoFallback(
      () =>
        getStaffAxiosClient().get("/clients/api/staff/trial-management/", {
          params,
        }),
      DEMO_TRIAL_MANAGEMENT,
      params
    ),

  fetchMlsPurchases: (params) =>
    withDemoFallback(
      () =>
        getStaffAxiosClient().get("/clients/api/staff/mls-purchases/", {
          params,
        }),
      DEMO_MLS_PURCHASES,
      params
    ),

  fetchMlsPurchaseDetail: (purchaseId) => {
    if (isAdminDemoMode()) {
      const row =
        DEMO_MLS_PURCHASES.find((item) => item.id === purchaseId) ||
        DEMO_MLS_PURCHASES[0]
      return Promise.resolve({ data: row })
    }
    return getStaffAxiosClient().get(
      `/clients/api/staff/mls-purchases/${purchaseId}/`
    )
  },

  /** Cancel an order nobody paid for; its payment link stops working. */
  cancelUnpaidMlsPurchase: (purchaseId) =>
    getStaffAxiosClient().post(
      `/clients/api/staff/mls-purchases/${purchaseId}/cancel-unpaid/`
    ),

  fetchSystemStatus: (params) => {
    if (isAdminDemoMode()) {
      return Promise.resolve({
        data: {
          checked_at: new Date().toISOString(),
          overall: "unknown",
          data_complete: true,
          environments: [],
        },
      })
    }
    return getStaffAxiosClient().get("/clients/api/staff/system-status/", {
      params,
    })
  },

  // MLS boards sold on CRM deals: { count, results, clients }.
  fetchMlsDealCredits: (params) =>
    withDemoFallback(
      () =>
        getStaffAxiosClient().get("/clients/api/staff/mls-deal-credits/", {
          params,
        }),
      [],
      params
    ),

  fetchMlsList: (params) =>
    withDemoFallback(
      () =>
        getStaffAxiosClient().get("/clients/api/staff/mls-list/", {
          params,
        }),
      [],
      params
    ),

  fetchMlsDetail: (mlsId) => {
    if (isAdminDemoMode()) {
      return Promise.resolve({
        data: {
          id: mlsId,
          name: "Demo MLS",
          visible: true,
          user_count: 0,
          users: [],
        },
      })
    }
    return getStaffAxiosClient().get(`/clients/api/staff/mls-list/${mlsId}/`)
  },

  assignMlsToUser: (mlsId, userId) => {
    if (isAdminDemoMode()) {
      return Promise.resolve({ data: { detail: "MLS assigned to user." } })
    }
    return getStaffAxiosClient().post(
      `/clients/api/staff/mls-list/${mlsId}/users/`,
      { user_id: userId }
    )
  },

  removeMlsFromUser: (mlsId, userId) => {
    if (isAdminDemoMode()) {
      return Promise.resolve({ data: null })
    }
    return getStaffAxiosClient().delete(
      `/clients/api/staff/mls-list/${mlsId}/users/${userId}/`
    )
  },

  updateTrialManagement: (subscriptionId, payload) => {
    if (isAdminDemoMode()) {
      const existing =
        DEMO_TRIAL_MANAGEMENT.find((item) => item.id === subscriptionId) ||
        DEMO_TRIAL_MANAGEMENT[0]
      const updated = { ...existing, ...payload }
      const index = DEMO_TRIAL_MANAGEMENT.findIndex(
        (item) => item.id === subscriptionId
      )
      if (index >= 0) DEMO_TRIAL_MANAGEMENT[index] = updated
      return Promise.resolve({ data: updated })
    }
    return getStaffAxiosClient().patch(
      `/clients/api/staff/trial-management/${subscriptionId}/`,
      payload
    )
  },
}

const PODS = "/pods/api/staff/pods/"

/** Pod Dialer setup (staff). No demo data: pods only exist against a real API. */
export const podsApi = {
  list: () => getStaffAxiosClient().get(PODS),
  create: (payload) => getStaffAxiosClient().post(PODS, payload),
  get: (podId) => getStaffAxiosClient().get(`${PODS}${podId}/`),
  update: (podId, payload) =>
    getStaffAxiosClient().patch(`${PODS}${podId}/`, payload),
  employees: () => getStaffAxiosClient().get("/pods/api/staff/employees/"),
  addMember: (podId, userId) =>
    getStaffAxiosClient().post(`${PODS}${podId}/members/`, { user_id: userId }),
  removeMember: (podId, memberId) =>
    getStaffAxiosClient().delete(`${PODS}${podId}/members/${memberId}/`),
  addClient: (podId, payload) =>
    getStaffAxiosClient().post(`${PODS}${podId}/clients/`, payload),
  updateClient: (podId, podClientId, payload) =>
    getStaffAxiosClient().patch(
      `${PODS}${podId}/clients/${podClientId}/`,
      payload
    ),
  removeClient: (podId, podClientId) =>
    getStaffAxiosClient().delete(`${PODS}${podId}/clients/${podClientId}/`),
  addBlock: (podId, payload) =>
    getStaffAxiosClient().post(`${PODS}${podId}/schedule/`, payload),
  removeBlock: (podId, blockId) =>
    getStaffAxiosClient().delete(`${PODS}${podId}/schedule/${blockId}/`),
  leadSummary: (podId) =>
    getStaffAxiosClient().get(`${PODS}${podId}/leads/summary/`),
  live: (podId) => getStaffAxiosClient().get(`${PODS}${podId}/live/`),
  sessionAction: (podId, action, hours) =>
    getStaffAxiosClient().post(`${PODS}${podId}/session/`, { action, hours }),
  addCampaign: (podId, podClientId, payload) =>
    getStaffAxiosClient().post(
      `${PODS}${podId}/clients/${podClientId}/campaigns/`,
      payload
    ),
  updateCampaign: (podId, campaignId, payload) =>
    getStaffAxiosClient().patch(
      `${PODS}${podId}/campaigns/${campaignId}/`,
      payload
    ),
  removeCampaign: (podId, campaignId) =>
    getStaffAxiosClient().delete(`${PODS}${podId}/campaigns/${campaignId}/`),
  numbers: (podId, campaignId) =>
    getStaffAxiosClient().get(
      `${PODS}${podId}/campaigns/${campaignId}/numbers/`
    ),
  setTextNumber: (podId, campaignId, numberId) =>
    getStaffAxiosClient().post(
      `${PODS}${podId}/campaigns/${campaignId}/numbers/${numberId}/text-number/`
    ),
  buyNumbers: (podId, campaignId, payload) =>
    getStaffAxiosClient().post(
      `${PODS}${podId}/campaigns/${campaignId}/numbers/`,
      payload
    ),
}

/** Runtime feature switches (only FEATURE_FLAG_ADMIN_EMAILS may change them). */
export const featuresApi = {
  get: () => getStaffAxiosClient().get("/clients/api/staff/features/"),
  set: (key, enabled) =>
    getStaffAxiosClient().post("/clients/api/staff/features/", {
      key,
      enabled,
    }),
}
